import { useCallback, useEffect, useRef, useState } from 'react'
import { subscribeToSelectionChanges } from '../api/word/office-service'
import { hasDocumentComments } from '../api/word/word-service'

// Selection changes fire on every cursor move, far more often than comments actually
// change. Debouncing collapses a burst of moves (dragging, arrow-key scrolling) into
// a single Word round-trip instead of one per event.
const CHECK_DEBOUNCE_MS = 500

// Word.run() inside hasDocumentComments() can silently hang when the host is busy
// (dialog open, focus lost, etc.), leaving isCheckRunningRef stuck at true forever.
// 5 s matches useMouseSelection's timeout while still guaranteeing the guard resets.
const COMMENTS_CHECK_TIMEOUT_MS = 5000

/**
 * Returns whether the current Word document has any comments at all - used to
 * disable the comments context badge when there is nothing to include.
 *
 * There is no stable, production-safe Word event for "a comment was added/removed":
 * Word.Body's onCommentAdded/onCommentDeleted are preview-only APIs (see
 * https://learn.microsoft.com/javascript/api/word/word.eventsource?view=word-js-preview),
 * which would require loading the beta office.js instead of the production one this
 * add-in ships with. Instead, this mirrors useMouseSelection's approach: piggyback on
 * the one event that *is* stable in production, DocumentSelectionChanged, and re-ask
 * Word for the ground truth (debounced, since that event fires far more often than
 * comments change).
 *
 * Known gap: a comment added/removed by another user during coauthoring on the same
 * document isn't picked up until the local user's selection changes again for an
 * unrelated reason. Acceptable here because this only drives a UI affordance - the
 * real comment content is always re-fetched fresh at send time in useChatContext.
 *
 * @param enabled - Skip the mount check and selection-change subscription entirely
 * when false, so callers behind a feature flag (e.g. the comments badge) don't pay
 * for Word.run() round-trips nobody will see the result of.
 */
export function useDocumentComments(enabled: boolean = true) {
  const [hasComments, setHasComments] = useState(false)

  // Mirrors useMouseSelection's in-flight guard: never run two Word.run() calls at
  // once. If a new selection event arrives mid-check, remember it and check once more
  // after the current call finishes, instead of stacking up parallel requests.
  const isCheckRunningRef = useRef(false)
  const anotherCheckWaitingRef = useRef(false)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const checkForComments = useCallback(async () => {
    if (isCheckRunningRef.current) {
      anotherCheckWaitingRef.current = true
      return
    }

    isCheckRunningRef.current = true
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    try {
      const timeout = new Promise<boolean>((resolve) => {
        timeoutId = setTimeout(() => resolve(false), COMMENTS_CHECK_TIMEOUT_MS)
      })
      setHasComments(await Promise.race([hasDocumentComments(), timeout]))
    } catch {
      // If the check fails for any reason, disable the badge rather than leave it
      // enabled for comments we can't actually confirm exist.
      setHasComments(false)
    } finally {
      clearTimeout(timeoutId)
      isCheckRunningRef.current = false
      if (anotherCheckWaitingRef.current) {
        anotherCheckWaitingRef.current = false
        void checkForComments()
      }
    }
  }, [])

  const onDocumentSelectionChange = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(() => {
      void checkForComments()
    }, CHECK_DEBOUNCE_MS)
  }, [checkForComments])

  useEffect(() => {
    if (!enabled) return

    // Check once on mount so the badge reflects reality before the user touches anything.
    void checkForComments()

    const unsubscribe = subscribeToSelectionChanges(onDocumentSelectionChange)
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      unsubscribe()
    }
  }, [enabled, checkForComments, onDocumentSelectionChange])

  return { hasComments }
}
